import React from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import Tabs from '../../../Presentation/components/ui/Tabs';
import Modal from '../../../Presentation/components/ui/Modal';
import {Package, Tags} from 'lucide-react';
import {useProductsManagement} from '../hooks/useProductsManagement';
import ProductDetailsModal from '../components/ProductDetailsModal';
import AddStockForm from '../../inventory/components/AddStockForm';
import AddProductModal from '../components/AddProductModal';
import EditProductModal from '../components/EditProductModal';
import {ProductsTabContent} from '../components/ProductsTabContent';
import {ClassificationsTabContent} from '../components/ClassificationsTabContent';
import {useAuth} from '../../auth/hooks/useAuth';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

type ActiveTab = 'products' | 'classifications';

const ProductsManagementPage: React.FC = () => {
  const {hasPermission} = useAuthorization();
  const {user} = useAuth();

  const {
    activeTab,
    classificationsSubTab,
    setClassificationsSubTab,
    isDetailsModalOpen,
    isAddStockFormOpen,
    isAddProductModalOpen,
    isEditProductModalOpen,
    selectedProductForDetails,
    selectedProductForStock,
    editProductId,
    setEditProductId,
    searchQuery,
    setSearchQuery,
    filteredProducts,
    brands,
    tags,
    productTypes,
    pagination,
    page,
    setPage,
    setPageSize,
    loading,
    fetchProducts,
    fetchBrands,
    fetchTags,
    fetchProductTypes,
    handleProductSelectForStock,
    handleAddStockSuccess,
    handleAddStockCancel,
    handleViewProductDetails,
    handleCloseDetailsModal,
    handleProductDeleted,
    handleAddProductSuccess,
    handleAddProductCancel,
    handleTabClick,
    openAddProductModal
  } = useProductsManagement();

  if (!hasPermission(PERMISSIONS.READ_PRODUCTS) && !hasPermission(PERMISSIONS.MANAGE_PRODUCTS)) {
    return (
      <SidebarLayout>
        <div className="p-6 min-h-screen rounded-xl bg-[#F2F3F5]">
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى إدارة المنتجات. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
        </div>
      </SidebarLayout>
    );
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPage(1);
    setPageSize(newPageSize);
  };

  const handleOpenAddProductModal = () => {
    if (brands.length === 0) fetchBrands();
    if (tags.length === 0) fetchTags();
    if (productTypes.length === 0) fetchProductTypes();
    openAddProductModal();
  };

  const tabItems = [
    hasPermission(PERMISSIONS.MANAGE_PRODUCTS) && {
      key: 'products' as const,
      label: 'إدارة المنتجات',
      description: 'عرض وإدارة المنتجات',
      icon: <Package className="w-5 h-5" />,
      badge: pagination?.total ?? 0,
      content: (
        <div className="p-6">
          <ProductsTabContent
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filteredProducts={filteredProducts}
            pagination={pagination ?? {}}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            onAddStock={handleProductSelectForStock}
            onViewDetails={handleViewProductDetails}
            onEditProduct={(p) => setEditProductId(p.id)}
            onAddProduct={handleOpenAddProductModal}
            currentUserId={user?.id ?? 0}
            loading={loading}
          />
        </div>
      )
    },
    hasPermission(PERMISSIONS.MANAGE_PRODUCTS) && {
      key: 'classifications' as const,
      label: 'تصنيفات المنتجات',
      description: 'البراندات، الأنواع، العلامات',
      icon: <Tags className="w-5 h-5" />,
      content: (
        <div className="p-6">
          <ClassificationsTabContent
            activeSubTab={classificationsSubTab}
            onSubTabChange={(tab) => setClassificationsSubTab(tab)}
          />
        </div>
      )
    }
  ].filter(Boolean) as {
    key: ActiveTab;
    label: string;
    description: string;
    icon: React.ReactNode;
    badge?: number;
    content: React.ReactNode;
  }[];

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div>
          <AppTitle className="text-2xl font-bold text-gray-900">إدارة المنتجات</AppTitle>
          <p className="text-gray-600 mt-1">إدارة المنتجات والتصنيفات</p>
        </div>

        <Card className="p-0 overflow-hidden shadow-sm">
          <Tabs
            items={tabItems}
            defaultActiveKey={activeTab}
            onChange={(key) => handleTabClick(key as ActiveTab)}
            variant="modern"
            className="flex-1"
          />
        </Card>

        {isDetailsModalOpen && selectedProductForDetails && (
          <ProductDetailsModal
            productId={selectedProductForDetails.id}
            isOpen={isDetailsModalOpen}
            onClose={handleCloseDetailsModal}
            onDeleted={handleProductDeleted}
          />
        )}

        {selectedProductForStock && isAddStockFormOpen && (
          <Modal
            open={isAddStockFormOpen}
            onClose={handleAddStockCancel}
            title="إضافة مخزون"
            width="max-w-4xl"
          >
            <AddStockForm
              product={selectedProductForStock}
              currentUserId={user?.id ?? 0}
              onSuccess={handleAddStockSuccess}
              onCancel={handleAddStockCancel}
            />
          </Modal>
        )}

        <AddProductModal
          isOpen={isAddProductModalOpen}
          onClose={handleAddProductCancel}
          onSuccess={handleAddProductSuccess}
        />

        <EditProductModal
          isOpen={!!editProductId}
          productId={editProductId}
          onClose={() => setEditProductId(null)}
          onSuccess={() => fetchProducts()}
        />
      </div>
    </SidebarLayout>
  );
};

export default ProductsManagementPage;
